import { useCallback, useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "medium" });

  const load = useCallback(() => api.get("/support/tickets").then(({ data }) => setTickets(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/support/tickets", form);
      toast.success("Ticket created — we reply within 24 hours");
      setOpen(false);
      setForm({ subject: "", message: "", priority: "medium" });
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="support-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Support</h1>
          <p className="text-sm text-slate-500">Tickets, NDR help and billing questions.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-ticket-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">
          <Plus className="h-4 w-4" /> New Ticket
        </button>
      </div>

      <div className="mt-6 space-y-4" data-testid="tickets-list">
        {tickets.map((t) => (
          <div key={t.ticket_id} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-shipany-navy">{t.subject}</h3>
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${t.status === "open" ? "bg-amber-50 text-amber-600" : t.status === "resolved" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{t.status}</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">{t.message}</p>
            {(t.replies || []).map((r, i) => (
              <div key={i} className="mt-3 rounded-lg bg-shipany-mist px-4 py-2.5 text-sm"><span className="font-semibold text-shipany-blue">Support:</span> {r.message}</div>
            ))}
            <p className="mt-2 text-xs text-slate-400">{fmtDateTime(t.created_at)} · Priority {t.priority}</p>
          </div>
        ))}
        {tickets.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">No tickets yet. We're here if you need us.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ticket-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">New Ticket</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3.5">
              <div><label className="text-xs font-semibold text-slate-500">Subject *</label>
                <input data-testid="ticket-subject" className={`${inputCls} mt-1`} placeholder="e.g. Pickup not scheduled" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Priority</label>
                <select data-testid="ticket-priority" className={`${inputCls} mt-1`} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  <option value="low">Low priority</option><option value="medium">Medium priority</option><option value="high">High priority</option>
                </select></div>
              <div><label className="text-xs font-semibold text-slate-500">Describe your issue *</label>
                <textarea data-testid="ticket-message" className={`${inputCls} mt-1`} rows={5} placeholder="Order/AWB number, what happened, when…" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
            </div>
            <button data-testid="ticket-submit-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Submit Ticket</button>
          </form>
        </div>
      )}
    </div>
  );
}
